import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2Off, Search, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();
const SYM = { INR: '₹', USD: '$', EUR: '€', GBP: '£', CAD: 'C$' };

const LeadOption = ({ lead, onPick, busy, testid }) => (
  <button type="button" onClick={() => onPick(lead)} disabled={busy}
    className="w-full text-left p-2 rounded border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
    data-testid={testid}>
    <div className="text-sm font-medium text-slate-900 truncate">{lead.full_name}</div>
    <div className="text-xs text-slate-500 truncate">{lead.phone || '—'} · {lead.email || 'no email'} · {lead.enrollment_status === 'enrolled' ? 'Enrolled' : lead.trial_payment_status === 'paid' ? 'Trial paid' : 'Lead'}</div>
  </button>
);

const OrphanRow = ({ row, onAttached }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/lead-search`, { params: { q: term }, withCredentials: true });
        setResults(res.data || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const attach = async (lead) => {
    if (!window.confirm(`Attach ${SYM[row.currency] || row.currency}${row.amount} to ${lead.full_name}?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/data-integrity/orphan-payments/attach`,
        { payment_id: row.payment_id, lead_id: lead.lead_id }, { withCredentials: true });
      toast.success(`Mapped to ${lead.full_name}`);
      onAttached(row.payment_id, res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Attach failed');
    } finally { setBusy(false); }
  };

  const when = row.captured_at ? new Date(row.captured_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`orphan-row-${row.payment_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900" data-testid={`orphan-amount-${row.payment_id}`}>
            {SYM[row.currency] || row.currency} {Number(row.amount || 0).toLocaleString()} <span className="text-xs font-normal text-slate-500">· {when} · {row.product_type || row.payment_type || 'payment'}</span>
          </div>
          <div className="text-xs text-slate-600 truncate">
            {row.customer_name ? `${row.customer_name} · ` : ''}{[...row.emails, ...row.phones].join(' · ') || 'no contact captured'}
            {row.dangling_lead_id && <span className="ml-1 text-amber-700">(lead {row.dangling_lead_id} no longer exists)</span>}
          </div>
          <div className="text-[11px] text-slate-400 font-mono">{row.payment_id}{row.razorpay_payment_id ? ` · ${row.razorpay_payment_id}` : ''}</div>
        </div>
      </div>
      {row.suggestions.length > 0 && (
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Suggested match</div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {row.suggestions.map((l) => <LeadOption key={l.lead_id} lead={l} onPick={attach} busy={busy} testid={`orphan-suggest-${row.payment_id}-${l.lead_id}`} />)}
          </div>
        </div>
      )}
      <div className="mt-2 relative">
        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a lead by name, phone or email to attach…"
          className="input-base pl-8 text-sm" data-testid={`orphan-search-${row.payment_id}`} />
      </div>
      {results.length > 0 && (
        <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2 max-h-48 overflow-y-auto" data-testid={`orphan-results-${row.payment_id}`}>
          {results.map((l) => <LeadOption key={l.lead_id} lead={l} onPick={attach} busy={busy} testid={`orphan-pick-${row.payment_id}-${l.lead_id}`} />)}
        </div>
      )}
    </div>
  );
};

const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: null },
];

const OrphanPaymentsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [preset, setPreset] = useState('30d');

  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.days === null) { setStartDate(''); setEndDate(''); } else { setStartDate(isoDaysAgo(p.days)); setEndDate(isoDaysAgo(0)); }
  };

  const load = async () => {
    if ((startDate && !endDate) || (!startDate && endDate)) { toast.error('Pick both dates, or clear both for all time'); return; }
    if (startDate && endDate && startDate > endDate) { toast.error('Start date must be before end date'); return; }
    setLoading(true);
    setShowAll(false);
    try {
      const params = startDate && endDate ? { start_date: startDate, end_date: endDate } : {};
      const res = await axios.get(`${API_URL}/api/data-integrity/orphan-payments`, { params, withCredentials: true, timeout: 60000 });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load orphan payments');
    } finally { setLoading(false); }
  };

  const onAttached = (paymentId) => setData((d) => d ? { total: d.total - 1, payments: d.payments.filter((p) => p.payment_id !== paymentId) } : d);
  const rows = data ? (showAll ? data.payments : data.payments.slice(0, 10)) : [];

  return (
    <div className="card mt-6" data-testid="orphan-payments-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Link2Off className="w-5 h-5 text-rose-600" /> Reconcile orphan payments</h3>
          <p className="text-sm text-slate-500">Paid payments that aren't linked to any lead (buyer used a different number/email, or the lead was deleted). Attach each to the right lead — trial/enrolment status updates automatically.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="orphan-date-range">
        <div className="flex gap-1">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => applyPreset(p)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md border ${preset === p.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              data-testid={`orphan-preset-${p.key}`}>{p.label}</button>
          ))}
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-0.5">From (capture date, IST)</label>
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset('custom'); }} className="input-base text-sm py-1.5" data-testid="orphan-start-date" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-0.5">To</label>
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset('custom'); }} className="input-base text-sm py-1.5" data-testid="orphan-end-date" />
        </div>
        <button onClick={load} disabled={loading} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="orphan-run-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Scanning…' : 'Find orphans'}
        </button>
      </div>
      {data && (
        <div className="text-sm text-slate-700 mb-3" data-testid="orphan-total">
          {data.total === 0 ? 'No orphan payments in this range — every paid payment is mapped to a lead.' : <><span className="font-semibold text-rose-700">{data.total}</span> orphan payment{data.total === 1 ? '' : 's'} found</>}
          <span className="text-xs text-slate-500 ml-2">{data.start_date && data.end_date ? `${data.start_date} → ${data.end_date}` : 'all time'}</span>
        </div>
      )}
      <div className="space-y-2" data-testid="orphan-list">
        {rows.map((r) => <OrphanRow key={r.payment_id} row={r} onAttached={onAttached} />)}
      </div>
      {data && data.payments.length > 10 && !showAll && (
        <button onClick={() => setShowAll(true)} className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-800" data-testid="orphan-show-all-btn">
          Show all {data.payments.length}
        </button>
      )}
    </div>
  );
};

export default OrphanPaymentsCard;
